// Some errors are transient and so a retry is worth attempting, other errors - usually those with a specific error
// message, and usually data related, are permanent - there is no point retrying these if we know they will fail again.
// This is similar to the sentryBeforeSendEventProcessor() but that also includes normal JS errors.
//
// Notes: we could update this to test for simple string matches and then regex matches like we do in
// sentryBeforeSendEventProcessor() and we could also inject the array of strings to match against so we could test
// against different targets in different scenarios but for now those both seem to be premature optimisations.

/**
 * An array of regex strings that we can use to identify non recoverable errors that should not be added to or retried
 * by the queue because we know they will always to fail.
 *
 * @type {RegExp[]}
 */
const nonRecoverableErrorMessageRegexesToCheckFor = [
    // A 400 error means the data is wrong. Sending it again won't help.
    new RegExp(/Bad Request 400/, 'i'),
    // Old style thirdPartyRef is in the format 10101739466173547
    // New style thirdPartyRef is in the format 1-1-2502251216236 or 1-1-2502251216236.2
    new RegExp(/Order '[A-Z0-9]+' with thirdPartyRef: '[0-9\-.]+' already exists/, 'i'),
    new RegExp(/Duplicate entry '[0-9\-.]+' for key 'thirdPartyRef'/, 'i'),
    new RegExp(/you can not redeem or unredeem more than 1 item/, 'i'),
    new RegExp(/a member cannot be unredeemed twice on the same day/, 'i'),
    new RegExp(/this membership has already been redeemed\/unredeemed today/, 'i'),
    new RegExp(/the trading session has already ended/, 'i'),
    new RegExp(/this order line cannot be redeemed\/unredeemed \(ticket is unredeemable\)/, 'i'),
    // This last one is for tests.
    new RegExp(/permanently failed/, 'i'),
];

const recoverableOrderCreationErrorMessageRegexes = [
    new RegExp(/deadlock/, 'i'),
];

/**
 * We want to display some errors in a slightly less scary way than the big red error.
 *
 * @type {RegExp[]}
 */
const errorsToDisplayAsWarnings = [
    new RegExp(/cannot over-redeem an order line/, 'i'),
];

/**
 * Check if this looks like a recoverable error - one we can automatically retry.
 *
 * @param {string} messageString
 *
 * @returns {boolean}
 */
const isRecoverableOrderCreationError = (messageString = '') => {
    if (!messageString) {
        return false;
    }

    for (let i = 0; i < recoverableOrderCreationErrorMessageRegexes.length; i++) {
        let result = messageString.match(recoverableOrderCreationErrorMessageRegexes[i]);
        if (result) {
            return true;
        }
    }

    return false;
};

/**
 * Return the matching error string if it exists.
 *
 * @param {string} messageString
 *
 * @return {string|boolean}
 */
const findNonRecoverableErrors = (messageString = '') => {
    return findErrorByType(nonRecoverableErrorMessageRegexesToCheckFor, messageString);
};

/**
 * Return the matching error string if it exists.
 *
 * @param {string} messageString
 *
 * @return {string|boolean}
 */
const findErrorsToDisplayAsWarnings = (messageString = '') => {
    return findErrorByType(errorsToDisplayAsWarnings, messageString);
};

/**
 * @private
 *
 * Find an error message by the specified type.
 *
 * @param {RegExp[]} errorType
 * @param {string} messageString
 *
 * @return {string|boolean}
 */
const findErrorByType = (errorType, messageString) => {
    if (!messageString) {
        return false;
    }
    // Uses for() instead of Array.find() in order to return the match not just the regex.
    // eslint-disable-next-line no-restricted-syntax, no-unused-vars
    for (const errorMessageRegex of errorType) {
        let result = messageString.match(errorMessageRegex);
        if (result) {
            return result[0];
        }
    }
    return false;
};

module.exports = {
    findNonRecoverableErrors,
    findErrorsToDisplayAsWarnings,
    isRecoverableOrderCreationError,
};
